import { normalizeQcm } from '../normalizers/normalizeQcm';
import { HtmlWithKatex } from '../helpers/renderKatex';
import PrintEnonce, { PrintImg } from '../helpers/PrintEnonce';
import { consignePour } from '../helpers/saisiePolicy';
import { reponseQcmCourte } from '../helpers/reponseLibre';
import { largeurMaxExpressionsHtml } from '../helpers/expressions';

const CHROME_LETTRE_PX = 44;

/** Style de la grille d'options : colonne minimale = plus longue expression
 *  insécable (+ lettre), seulement si elle dépasse le minimum CSS (100 px). */
export const styleGrilleOptions = (options) => {
    const besoin = options.reduce(
        (m, o) => (o.kind === 'text' ? Math.max(m, largeurMaxExpressionsHtml(o.content)) : m),
        0,
    ) + CHROME_LETTRE_PX;
    return besoin > 100 ? { '--ep-opt-besoin': `${Math.round(besoin)}px` } : undefined;
};

/**
 * Renderer print d'un QCM (simple ou multiple), affichage standalone.
 *
 * Grille des options auto-adaptative (CSS `auto-fit + minmax(100px, 1fr)`) :
 * le navigateur pack le max de colonnes tenant dans la largeur de la card.
 * Seul override JS = `ep-qcm-long-choices` quand choix moyens > 30 chars
 * (rédactionnels) → force 1 colonne pour lisibilité.
 * Les images passent sur `ep-qcm-visual` (minmax 140px).
 *
 * `reponseLibre` (axe format de l'aperçu) : les choix textuels sont masqués
 * et remplacés par des lignes de réponse (1 si la bonne réponse est courte,
 * 3 sinon). Un QCM à images garde ses choix quoi qu'il arrive.
 */
/**
 * Corps d'un QCM (grille de choix, ou lignes de réponse en libre), sans
 * header ni consigne ni énoncé. Partagé par le single et les blocs à figure
 * partagée (`PrintFigureGroup`). `norm` = résultat de `normalizeQcm`.
 */
export const QcmBody = ({ payload, norm, saisieMode = 'sur_la_feuille', libre = false }) => {
    const { options, hasImages } = norm;

    // Grille auto-fit par défaut ; seul override Confort = `ep-qcm-long-choices`
    // quand les choix sont rédactionnels (>30c en moyenne).
    const avgLen = options.reduce(
        (s, o) => s + (o.kind === 'text' ? (o.content?.length || 0) : 100),
        0
    ) / (options.length || 1);
    // Colonnage par seuils sur l'option LA PLUS LONGUE (mode Compact
    // uniquement — les classes ep-qcm-cols-* n'ont d'effet que sous
    // .ep-compact ; le Confort garde son auto-fit).
    const maxLen = options.reduce(
        (m, o) => Math.max(m, o.kind === 'text' ? (o.content?.length || 0) : 100),
        0
    );
    const colsClass = maxLen < 25 ? 'ep-qcm-cols-4' : maxLen < 60 ? 'ep-qcm-cols-2' : 'ep-qcm-cols-1';
    const classes = ['ep-qcm-options'];
    if (hasImages) classes.push('ep-qcm-visual');
    else {
        if (avgLen > 30) classes.push('ep-qcm-long-choices');
        classes.push(colsClass);
    }
    const gridClass = classes.slice(1).join(' ');
    // Expressions jamais coupées : la colonne minimale de la grille = la plus
    // longue expression insécable d'une option + chrome de la lettre (44 px),
    // sinon elle déborderait sur la case voisine (cf. .ep-qcm-options).
    const styleGrille = styleGrilleOptions(options);

    const surFeuille = saisieMode !== 'sur_le_cahier';
    const courte = libre && reponseQcmCourte(payload);

    if (libre) {
        if (!surFeuille) return null;
        return courte ? (
            <div className="ep-answer-lines ep-answer-lines-short">
                <div className="ep-answer-line" />
            </div>
        ) : (
            <div className="ep-answer-lines">
                <div className="ep-answer-line" />
                <div className="ep-answer-line" />
                <div className="ep-answer-line" />
            </div>
        );
    }
    return (
        <ul className={`ep-qcm-options ${gridClass}`} style={styleGrille}>
            {options.map((opt) => (
                <li key={opt.letter} className="ep-qcm-option">
                    <span className="ep-qcm-letter">{opt.letter}</span>
                    {opt.kind === 'image'
                        ? <PrintImg className="ep-qcm-option-img" src={opt.content} alt={opt.alt} />
                        : <HtmlWithKatex className="ep-qcm-option-text" html={opt.content} />
                    }
                </li>
            ))}
        </ul>
    );
};

const PrintQcm = ({ payload, num, saisieMode = 'sur_la_feuille', reponseLibre = false }) => {
    const norm = normalizeQcm(payload, num);
    const libre = reponseLibre && !norm.hasImages;
    // Feuille : « Entoure… » ; cahier : « Écris la lettre… » — les choix
    // restent affichés dans les deux modes (support de la réponse écrite).
    const consigne = consignePour(payload, saisieMode, { libre });

    return (
        <div className="ep-exo">
            <span className="ep-exo-num">{num}</span>
            <p className="ep-exo-consigne">{consigne}</p>
            <PrintEnonce texte={norm.question} elements={payload?.elements_additionnels?.elements} />
            <QcmBody payload={payload} norm={norm} saisieMode={saisieMode} libre={libre} />
        </div>
    );
};

export default PrintQcm;
